import { useEffect, useState } from 'react'
import { Box, Image, Tooltip, UnstyledButton } from '@mantine/core'
import { useFAB } from './useFAB'
import { hasSeenFAB } from './FABProvider'
import { FABMenu } from './FABMenu'
import classes from './AnchorFAB.module.css'
import { useAuthLayout } from '@/features/layout/context'

interface AnchorFABProps {
  /** Control visibility externally (default: true) */
  visible?: boolean
}

export function AnchorFAB({ visible = true }: AnchorFABProps) {
  const { actions, badge, pulse, openMenu, closeMenu } = useFAB()
  const { footerHeight, showFooter } = useAuthLayout()
  const [showTooltip, setShowTooltip] = useState(false)
  const [isFirstVisit, setIsFirstVisit] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)

  // Track hydration - FAB is client-only to avoid SSR mismatch
  // (actions are set via useEffect which doesn't run on server)
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Check first visit on mount
  useEffect(() => {
    const seen = hasSeenFAB()
    if (!seen) {
      setIsFirstVisit(true)
      setShowTooltip(true)
    }
  }, [])

  // Don't render during SSR or if no actions or not visible
  // This prevents hydration mismatch since actions are set client-side via useEffect
  if (!isHydrated || !visible || actions.length === 0) {
    return null
  }

  const singleAction = actions.length === 1 ? actions[0] : null

  // Dismiss tooltip and first-visit state on FAB click
  function handleFABClick() {
    setShowTooltip(false)
    setIsFirstVisit(false)
    if (singleAction) {
      closeMenu()
      singleAction.onPress()
    } else {
      openMenu()
    }
  }

  const showBadge = badge || isFirstVisit
  const showPulse = pulse || isFirstVisit

  const fabClasses = [classes.fab, showPulse && classes.pulse]
    .filter(Boolean)
    .join(' ')

  // Dynamic bottom offset based on footer height
  // Position above footer when visible, otherwise use default 16px
  const bottomOffset = footerHeight > 0 ? footerHeight + 16 : 16

  return (
    <>
      <Tooltip
        label={singleAction ? 'Refine your plan' : 'Your Anchor: tools & more'}
        opened={showTooltip}
        position="left"
        withArrow
        arrowSize={8}
        offset={12}
      >
        <UnstyledButton
          className={fabClasses}
          onClick={handleFABClick}
          aria-label={singleAction ? singleAction.label : 'Open actions menu'}
          style={{ bottom: bottomOffset }}
        >
          <Image src="/logo.png" alt="" w={32} h={32} />
          {showBadge && (
            <Box
              className={classes.badge}
              aria-live="polite"
              aria-label="New actions available"
            />
          )}
        </UnstyledButton>
      </Tooltip>

      <FABMenu />
    </>
  )
}
